import React from 'react';
import { Row, Col, Card, Empty, Typography } from 'antd';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { RevenueDataPoint, SalesTypeData } from '@/services/dashboard.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { uiPlainCurrencyLabel } from '@/constants/uaDirhamDisplay';
import { EnterpriseTokens } from '@/theme/enterprise-tokens';

const { Text } = Typography;

export interface ExecutiveTrendsRowProps {
  revenueTrend: RevenueDataPoint[];
  salesByType: SalesTypeData[];
  loading: boolean;
}

export const ExecutiveTrendsRow: React.FC<ExecutiveTrendsRowProps> = ({
  revenueTrend,
  salesByType,
  loading,
}) => {
  const { baseCurrency } = useCurrency();
  const sym =
    uiPlainCurrencyLabel(baseCurrency?.symbol, baseCurrency?.currency_code || baseCurrency?.code) ||
    (baseCurrency?.currency_code ?? '');

  const hasRevenue = revenueTrend.length > 0;
  const hasPie = salesByType.length > 0 && salesByType.some((s) => Number(s.value) > 0);

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: EnterpriseTokens.spacing.xl }}>
      <Col xs={24} lg={16}>
        <Card
          title="Revenue and profit trend"
          bordered={false}
          style={{ border: EnterpriseTokens.workspace.borderSubtle }}
          loading={loading}
        >
          {!hasRevenue ? (
            <Empty
              description={
                <Text type="secondary">
                  No trend series yet. Data appears when invoices are posted for the period, or when the analytics feed is
                  configured.
                </Text>
              }
            />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value: number) => `${sym} ${Number(value).toLocaleString()}`} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#145A54" strokeWidth={2} name="Revenue" />
                <Line type="monotone" dataKey="profit" stroke="#06D6A0" strokeWidth={2} name="Profit" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <Card
          title="Mix by category"
          bordered={false}
          style={{ border: EnterpriseTokens.workspace.borderSubtle }}
          loading={loading}
        >
          {!hasPie ? (
            <Empty description={<Text type="secondary">No breakdown available for the selected period.</Text>} />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={salesByType}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                  outerRadius={88}
                  dataKey="value"
                >
                  {salesByType.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color || '#8c8c8c'} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </Col>
    </Row>
  );
};
